import { useState } from 'react';
import { useAdminClientesAsesoria, type ClienteAsesoria } from '../../../hooks/useAdminClientesAsesoria';

const PAGE_SIZE = 10;
const PLANES = ['Esencial', 'Premium', 'Integral'];

export default function AdminClientesAsesoria() {
  const { data, add, update, remove, refresh, loading } = useAdminClientesAsesoria();
  const [editing, setEditing] = useState<ClienteAsesoria | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [search, setSearch] = useState('');
  const [planFilter, setPlanFilter] = useState('todos');
  const [formError, setFormError] = useState<string | null>(null);
  const [page, setPage] = useState(1);

  const filtered = data.filter((c) => {
    const matchSearch =
      c.nombre.toLowerCase().includes(search.toLowerCase()) ||
      (c.email?.toLowerCase().includes(search.toLowerCase()) ?? false);
    const matchPlan = planFilter === 'todos' || c.plan_contratado === planFilter;
    return matchSearch && matchPlan;
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const startIdx = (safePage - 1) * PAGE_SIZE;
  const paged = filtered.slice(startIdx, startIdx + PAGE_SIZE);

  const handleSave = async (formData: FormData) => {
    setFormError(null);
    const nombre = (formData.get('nombre') as string)?.trim();
    const email = (formData.get('email') as string)?.trim() || null;
    const telefono = (formData.get('telefono') as string)?.trim() || null;
    const plan_contratado = (formData.get('plan_contratado') as string) || null;
    const fecha_inicio = (formData.get('fecha_inicio') as string) || null;
    const notas = (formData.get('notas') as string)?.trim() || null;

    if (!nombre) { setFormError('El nombre es obligatorio.'); return; }

    const payload: Omit<ClienteAsesoria, 'id' | 'created_at'> = {
      nombre, email, telefono, plan_contratado, fecha_inicio, notas, activo: true,
    };

    if (editing) {
      await update(editing.id, payload);
      setEditing(null);
    } else {
      await add(payload);
      setIsAdding(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <h2 className="font-serif text-brand-black text-2xl font-medium">
          Clientes Asesoría{' '}
          <span className="text-brand-gray-400">({data.length})</span>
        </h2>
        <div className="flex items-center gap-3">
          <select
            value={planFilter}
            onChange={(e) => { setPlanFilter(e.target.value); setPage(1); }}
            className="bg-white border border-brand-gray-200 rounded-lg px-4 py-2.5 font-sans text-sm focus:outline-none focus:border-brand-black"
          >
            <option value="todos">Todos los planes</option>
            {PLANES.map((p) => (
              <option key={p} value={p}>{p}</option>
            ))}
          </select>
          <input
            type="text"
            placeholder="Buscar cliente..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className="w-full sm:w-64 bg-white border border-brand-gray-200 rounded-lg px-4 py-2.5 font-sans text-sm focus:outline-none focus:border-brand-black"
          />
          <button
            onClick={() => { setIsAdding(true); setEditing(null); setFormError(null); }}
            className="bg-brand-black text-white font-sans text-sm px-5 py-2.5 rounded-lg hover:bg-brand-gray-600 transition-colors whitespace-nowrap"
          >
            + Nuevo cliente
          </button>
          <button
            onClick={refresh}
            className="w-10 h-10 rounded-lg bg-white border border-brand-gray-200 flex items-center justify-center text-brand-gray-600 hover:text-brand-black hover:border-brand-black transition-colors"
            title="Recargar"
          >
            <i className="ri-refresh-line" />
          </button>
        </div>
      </div>

      {/* Formulario */}
      {(isAdding || editing) && (
        <form
          onSubmit={(e) => { e.preventDefault(); handleSave(new FormData(e.currentTarget)); }}
          className="bg-white border border-brand-gray-200 rounded-2xl p-6 space-y-5"
        >
          <div className="flex items-center justify-between">
            <h3 className="font-serif text-brand-black text-lg">
              {editing ? 'Editar Cliente' : 'Nuevo Cliente'}
            </h3>
            <button
              type="button"
              onClick={() => { setEditing(null); setIsAdding(false); setFormError(null); }}
              className="w-8 h-8 flex items-center justify-center text-brand-gray-400 hover:text-brand-black"
            >
              <i className="ri-close-line text-xl" />
            </button>
          </div>
          {formError && (
            <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 flex items-start gap-2">
              <i className="ri-error-warning-line text-red-500 mt-0.5" />
              <p className="font-sans text-red-600 text-sm">{formError}</p>
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">Nombre *</label>
              <input name="nombre" defaultValue={editing?.nombre ?? ''} required className="mt-1 w-full border border-brand-gray-200 rounded-lg px-3 py-2.5 font-sans text-sm focus:outline-none focus:border-brand-black" />
            </div>
            <div>
              <label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">Email</label>
              <input name="email" type="email" defaultValue={editing?.email ?? ''} className="mt-1 w-full border border-brand-gray-200 rounded-lg px-3 py-2.5 font-sans text-sm focus:outline-none focus:border-brand-black" />
            </div>
            <div>
              <label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">Teléfono</label>
              <input name="telefono" defaultValue={editing?.telefono ?? ''} className="mt-1 w-full border border-brand-gray-200 rounded-lg px-3 py-2.5 font-sans text-sm focus:outline-none focus:border-brand-black" />
            </div>
            <div>
              <label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">Plan contratado</label>
              <select name="plan_contratado" defaultValue={editing?.plan_contratado ?? ''} className="mt-1 w-full border border-brand-gray-200 rounded-lg px-3 py-2.5 font-sans text-sm focus:outline-none focus:border-brand-black bg-white">
                <option value="">Sin plan</option>
                {PLANES.map((p) => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">Fecha inicio</label>
              <input name="fecha_inicio" type="date" defaultValue={editing?.fecha_inicio ?? ''} className="mt-1 w-full border border-brand-gray-200 rounded-lg px-3 py-2.5 font-sans text-sm focus:outline-none focus:border-brand-black" />
            </div>
          </div>
          <div>
            <label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">Notas</label>
            <textarea name="notas" defaultValue={editing?.notas ?? ''} rows={3} className="mt-1 w-full border border-brand-gray-200 rounded-lg px-3 py-2.5 font-sans text-sm focus:outline-none focus:border-brand-black resize-none" />
          </div>
          <div className="flex items-center gap-3 pt-2">
            <button type="submit" disabled={loading} className="bg-brand-black text-white font-sans text-sm px-6 py-2.5 rounded-lg hover:bg-brand-gray-600 transition-colors whitespace-nowrap disabled:opacity-60">
              {loading ? 'Guardando...' : 'Guardar'}
            </button>
            <button type="button" onClick={() => { setEditing(null); setIsAdding(false); setFormError(null); }} className="border border-brand-gray-200 text-brand-black font-sans text-sm px-6 py-2.5 rounded-lg hover:border-brand-black transition-colors whitespace-nowrap">
              Cancelar
            </button>
          </div>
        </form>
      )}

      {/* Tabla */}
      <div className="bg-white border border-brand-gray-200 rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-brand-gray-50">
              <tr>
                <th className="text-left font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium px-5 py-3">Nombre</th>
                <th className="text-left font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium px-5 py-3">Plan</th>
                <th className="text-left font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium px-5 py-3">Inicio</th>
                <th className="text-left font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium px-5 py-3">Contacto</th>
                <th className="text-left font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium px-5 py-3">Estado</th>
                <th className="text-right font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium px-5 py-3">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-brand-gray-200">
              {paged.map((cliente) => (
                <tr key={cliente.id} className="hover:bg-brand-gray-50/50 transition-colors">
                  <td className="px-5 py-4">
                    <p className="font-sans text-brand-black text-sm font-medium">{cliente.nombre}</p>
                    {cliente.notas && (
                      <p className="font-sans text-brand-gray-400 text-xs mt-0.5 line-clamp-1 max-w-[280px]">{cliente.notas}</p>
                    )}
                  </td>
                  <td className="px-5 py-4">
                    {cliente.plan_contratado ? (
                      <span className="inline-block px-2.5 py-1 rounded-full font-sans text-[11px] uppercase tracking-wider font-medium bg-brand-sage/20 text-brand-black">
                        {cliente.plan_contratado}
                      </span>
                    ) : (
                      <span className="font-sans text-brand-gray-400 text-xs">Sin plan</span>
                    )}
                  </td>
                  <td className="px-5 py-4 font-sans text-brand-gray-600 text-sm">
                    {cliente.fecha_inicio
                      ? new Date(cliente.fecha_inicio).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' })
                      : '—'}
                  </td>
                  <td className="px-5 py-4">
                    {cliente.email && <p className="font-sans text-brand-gray-600 text-xs">{cliente.email}</p>}
                    {cliente.telefono && <p className="font-sans text-brand-gray-500 text-xs mt-0.5">{cliente.telefono}</p>}
                  </td>
                  <td className="px-5 py-4">
                    {cliente.activo ? (
                      <span className="inline-block w-2 h-2 rounded-full bg-brand-sage mr-1.5" />
                    ) : (
                      <span className="inline-block w-2 h-2 rounded-full bg-brand-gray-400 mr-1.5" />
                    )}
                    <span className="font-sans text-brand-gray-600 text-xs">{cliente.activo ? 'Activo' : 'Inactivo'}</span>
                  </td>
                  <td className="px-5 py-4 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => { setEditing(cliente); setIsAdding(false); setFormError(null); }}
                        className="w-8 h-8 rounded-lg bg-brand-gray-100 flex items-center justify-center text-brand-gray-600 hover:bg-brand-black hover:text-white transition-colors"
                        title="Editar"
                      >
                        <i className="ri-pencil-line" />
                      </button>
                      <button
                        onClick={async () => {
                          if (!confirm(`¿Eliminar al cliente "${cliente.nombre}"?`)) return;
                          await remove(cliente.id);
                        }}
                        className="w-8 h-8 rounded-lg bg-brand-gray-100 flex items-center justify-center text-brand-gray-600 hover:bg-red-600 hover:text-white transition-colors"
                        title="Eliminar"
                      >
                        <i className="ri-delete-bin-line" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {filtered.length === 0 && (
          <div className="p-10 text-center">
            <p className="font-sans text-brand-gray-400 text-sm">No se encontraron clientes</p>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between bg-white border border-brand-gray-200 rounded-2xl px-5 py-3">
          <div className="font-sans text-brand-gray-500 text-sm">
            Mostrando {startIdx + 1}–{Math.min(startIdx + PAGE_SIZE, filtered.length)} de {filtered.length}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={safePage <= 1} className="w-8 h-8 rounded-lg border border-brand-gray-200 flex items-center justify-center text-brand-gray-600 hover:border-brand-black hover:text-brand-black transition-colors disabled:opacity-40">
              <i className="ri-arrow-left-s-line" />
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
              <button key={p} onClick={() => setPage(p)} className={`w-8 h-8 rounded-lg font-sans text-sm flex items-center justify-center transition-colors ${p === safePage ? 'bg-brand-black text-white' : 'border border-brand-gray-200 text-brand-gray-600 hover:border-brand-black hover:text-brand-black'}`}>
                {p}
              </button>
            ))}
            <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={safePage >= totalPages} className="w-8 h-8 rounded-lg border border-brand-gray-200 flex items-center justify-center text-brand-gray-600 hover:border-brand-black hover:text-brand-black transition-colors disabled:opacity-40">
              <i className="ri-arrow-right-s-line" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}